<div
  class="create-post-container"
  [class.create-post-active]="createPostActive"
  (onFileDrop)="droppedFile($event)"
  ng2FileDrop
  [uploader]="uploader"
  (fileOver)="fileOverBase($event)"
  [class.hovering]="hasBaseDropZoneOver"
>
  @if (channelPostingLocked && postGate.ok && !betaStringId) {
    <!-- This channel limits who can start posts (`posting_groups`). Replies are still open. -->
    <div class="channel-posting-locked">
      <fa-icon icon="lock" aria-hidden="true" class="m-r-5"></fa-icon>
      {{ "com.channel_posting_restricted" | translate: { channel: (lockedChannelTitle | localize: selectedChannel?.string_id) } }}
    </div>
  } @else if (postGate.ok || betaStringId || (awaitingCommunity && user)) {
    @if (!createPostActive) {
      <div
        class="create-post-trigger"
        tabindex="0"
        role="button"
        [attr.aria-label]="'com.start_new_post' | translate"
        (click)="postInputFocus('post')"
        (keydown.enter)="postInputFocus('post'); $event.preventDefault()"
        (keydown.space)="postInputFocus('post'); $event.preventDefault()"
      >
        {{ "com.start_new_post" | translate }}
      </div>
    }
    @if (createPostActive) {
      <!--
        Where the post goes, first: the community (when there's a choice), its channels, and
        close. Channels used to sit at the bottom beside Send, far from the community they
        belong to; together they read as one "posting in" line.
      -->
      <div class="create-post-where">
        <div class="create-post-where__targets">
          @if (communityChoices && communityChoices.length > 1) {
            <ng-container *ngTemplateOutlet="communityChoice"></ng-container>
            @if (awaitingCommunity) {
              <!-- Asked first: the channels, signature and posting rules all follow from it. -->
              <span class="create-post-where__hint" id="create-post-community-hint">Choose a community to post in</span>
            }
          } @else if (!betaStringId && postingInTitle) {
            <!-- No choice to make: still say where it's going. -->
            <span class="create-post-community">
              <span class="create-post-community__label">{{ "com.post_in" | translate }}</span>
              <span class="create-post-community__fixed">
                <span class="create-post-community__dot" [style.background-color]="postingInAccent || null" aria-hidden="true"></span>
                {{ postingInTitle }}
              </span>
            </span>
          }
          @if (topic) {
            <span class="create-post-selected-channel">{{ topic.title }}</span>
          }
          @if (!betaStringId && !topic && !selectedChannels.length && !awaitingCommunity) {
            <span class="create-post-selected-channel create-post-selected-channel--general">{{ "com.skills.general" | translate }}</span>
          }
          @for (channel of selectedChannels; track channel) {
            <span class="create-post-selected-channel">
              # {{ betaStringId ? channel.title : (channel.title | localize: channel.string_id) }}
              <button type="button" class="m-l-5 fa-button" (click)="removeChannel(channel)">
                <fa-icon icon="times" aria-hidden="true"></fa-icon>
                <span class="sr-only">{{ "com.remove_channel_named" | translate: { name: betaStringId ? channel.title : (channel.title | localize: channel.string_id) } }}</span>
              </button>
            </span>
          }
          @if ((!selectedChannels || selectedChannels.length < 3) && !awaitingCommunity) {
            <button type="button" class="add-channel-button" aria-controls="channel-list" [attr.aria-expanded]="shouldShowChannelSelector" (click)="toggleChannelSelector()">
              <fa-icon icon="plus" aria-hidden="true"></fa-icon>
              {{ "com.add_channel" | translate }}
            </button>
          }
          @if (shouldShowChannelSelector) {
            <!-- Escape closes it and returns to Add channel; see `closeChannelSelector`. -->
            <div
              class="channel-selector channel-selector--solid"
              id="channel-list"
              role="group"
              [attr.aria-label]="'com.add_channel' | translate"
              (clickOutside)="shouldShowChannelSelector = false"
              (keydown.escape)="closeChannelSelector(); $event.stopPropagation()"
            >
              @if (!betaStringId) {
                <div class="channel-list overflow-auto h-100">
                  @for (skill of postableChannels; track skill) {
                    <button type="button" class="channel-button bold pointer t-14 p-1" (click)="addChannel(skill, true)">
                      <fa-icon icon="hashtag" class="c-lblue" aria-hidden="true"></fa-icon>
                      {{ skill.title | localize: skill.string_id }}
                    </button>
                  }
                </div>
              }
              @if (betaStringId) {
                <div class="channel-list overflow-auto h-100">
                  @for (tag of betaService.communityTags; track tag) {
                    <button type="button" class="channel-button bold pointer t-14 p-1" (click)="addChannel(tag, true)">
                      <fa-icon icon="hashtag" class="c-lblue" aria-hidden="true"></fa-icon>
                      {{ tag.title }}
                    </button>
                  }
                </div>
              }
            </div>
          }
        </div>
        <button type="button" class="create-post-close" (click)="cancelCreatePost()">
          <fa-icon icon="times" aria-hidden="true"></fa-icon>
          <span class="sr-only">cancel create post</span>
        </button>
      </div>

      <div class="create-post-fields">
        <!-- The trigger already said "Start a new post"; the label stays for screen readers. -->
        <label id="start-post-label" class="sr-only" for="post-input">{{ "com.start_new_post" | translate }}</label>
        <!-- Beta programs, and any community whose admin turned on `subject_required`. The
           same flag `createPost()` validates against — see `showSubject`. -->
        @if (showSubject) {
          <div class="m-b-10">
            <label id="post-subject-label" class="sr-only" for="post-subject-input">{{ "com.enter_post_subject" | translate }}</label>
            <myp-message-input
              #subjectInput
              [type]="'span'"
              inputID="post-subject-input"
              (valueChanged)="postSubjectContentChanged($event)"
              (blur)="postInputBlur()"
              [placeholder]="'com.enter_post_subject' | translate"
              [backgroundColor]="appService.activeTheme == 'dark' ? '#2e2e2e' : '#e9e9e9'"
              [shouldDetectContent]="false"
              [includeSendButton]="false"
              [mentionSource]="betaStringId || !mentionsEnabled ? false : 'community_profiles'"
              ariaLabelBy="post-subject-label"
              [ariaDescribedBy]="subjectDescribedBy"
            ></myp-message-input>
          </div>
        }
        <myp-message-input
          #msgInput
          [type]="'span'"
          inputID="post-input"
          (valueChanged)="postContentChanged($event)"
          (returnPressed)="sendPost()"
          (blur)="postInputBlur()"
          [useQuill]="!!betaStringId"
          [placeholder]="betaStringId ? ('com.enter_post_body' | translate) : ('com.start_typing' | translate)"
          [shouldDetectContent]="true"
          [includeSendButton]="false"
          [backgroundColor]="appService.activeTheme == 'dark' ? '#2e2e2e' : '#e9e9e9'"
          [mentionSource]="betaStringId || !mentionsEnabled ? false : 'community_profiles'"
          ariaLabelBy="start-post-label"
          [ariaDescribedBy]="bodyDescribedBy"
          (openUploadImage)="attachFile(false, $event)"
        ></myp-message-input>
        <!-- Why the last Send didn't go (also said in the alert), tied to the field at fault. -->
        <span id="create-post-error" class="sr-only">{{ fieldErrorText }}</span>
      </div>

      <!--
        Signature preview (DENG-847), under a rule where it will land in the sent post. NOT
        injected into the editor, so the member can't half-delete it; the toolbar's switch
        turns it on and off for this post. `sendPost` appends it via `_withSignature`. Beta
        posts are excluded: signatures are per community.
      -->
      @if (!betaStringId && signature) {
        <div class="create-post-signature" [class.create-post-signature--off]="!signatureEnabled">
          <span class="create-post-signature__text">{{ signature }}</span>
          <button type="button" class="create-post-signature__link" [attr.aria-label]="'com.edit_signature' | translate" (click)="openSignatureSettings()">Edit</button>
        </div>
      }

      <!--
        A second mention dropdown used to be declared here, bound to
        `elligibleMentions` / `selectMention` — neither of which exists on this
        component, so it never rendered. The real picker is the floating one
        owned by <myp-message-input>.
      -->
      @if (msgInput.selectedArticlesInMessage && msgInput.selectedArticlesInMessage.length) {
        <div>
          @for (item of msgInput.selectedArticlesInMessage; track item) {
            <button class="comment-article-attachment" (click)="showArticle(item)">
              <fa-icon icon="newspaper" class="icon"></fa-icon>
              <div>
                <span class="bold">{{ item.article.name }}</span>
                <br />
                <span class="t-12 text-muted">{{ item.article.created_at_pretty }}</span>
              </div>
              <button class="close-btn fa-button" (click)="removeArticle(item, $event)" aria-label="remove article">
                <fa-icon icon="times-circle" aria-hidden="true"></fa-icon>
              </button>
            </button>
          }
        </div>
      }
      @if (msgInput.selectedLearnContentInMessage && msgInput.selectedLearnContentInMessage.length) {
        <div class="m-y-20">
          @for (item of msgInput.selectedLearnContentInMessage; track item) {
            @if (item.content.type == "video") {
              <div class="grid-container wider pad-10">
                <myp-video-grid-item [showBorder]="true" [video]="item.content" [showRemoveFromPost]="true" (removeFromPostClicked)="removeLearnContent(item, $event)"></myp-video-grid-item>
              </div>
            }
            @if (item.content.type != "video") {
              <myp-learn-list-item
                [shouldShowButtons]="false"
                [showRemoveFromPost]="true"
                [item]="item.content"
                [preventNav]="true"
                (removeFromPostClicked)="removeLearnContent(item, $event)"
              ></myp-learn-list-item>
            }
          }
        </div>
      }
      @if (msgInput.publicFileLinksInMessage && msgInput.publicFileLinksInMessage.length) {
        <div class="m-y-20">
          @for (item of msgInput.publicFileLinksInMessage; track item) {
            @for (item of msgInput.publicFileLinksInMessage; track item) {
              <div class="comment-article-attachment">
                <fa-icon [icon]="workspaceService.audioFiles[item.publicFile.extension] ? 'play-circle' : 'file'" class="icon"></fa-icon>
                <div>
                  <span class="bold">{{ item.publicFile.filename }}</span>
                  <br />
                  <span class="t-12 text-muted">{{ item.publicFile.description }}</span>
                </div>
                <button type="button" class="close-btn fa-button" (click)="removePublicFile(item, $event)" aria-label="Remove public file">
                  <fa-icon icon="times-circle" aria-hidden="true"></fa-icon>
                </button>
              </div>
            }
          }
        </div>
      }
      @if (msgInput.messageEmbeds && msgInput.messageEmbeds.length) {
        <div class="d-flex flex-column">
          @for (embed of msgInput.messageEmbeds; track embed) {
            <div class="message-embed message-embed--{{ embed.type }} message-embed--{{ embed.subtype }}">
              <button type="button" class="close-btn fa-button" (click)="removeEmbed(embed, $event)" aria-label="Remove Embed">
                <fa-icon icon="times-circle" aria-hidden="true"></fa-icon>
              </button>
              <iframe width="560" height="315" [src]="embed.embedSrc" frameborder="0" allowfullscreen></iframe>
            </div>
          }
        </div>
      }
      @if (uploadingFiles && uploadingFiles.length && createPostActive) {
        <div class="w-100">
          @for (file of uploadingFiles; track file) {
            <span class="text-muted t-14">{{ "general.uploading" | translate }}: {{ file.filename }} {{ file.progress }}%</span>
            <div class="post-progress-bar" [ngStyle]="{ width: (file.progress ? file.progress : 0) + '%' }" aria-hidden="true"></div>
          }
        </div>
      }
      @if (uploadedAttachments && uploadedAttachments.length && createPostActive) {
        <div class="d-flex flex-wrap">
          @for (attachment of uploadedAttachments; track attachment) {
            <div class="attachment">
              @if (workspaceService.imageFiles[attachment.extension]) {
                <img [src]="attachment.url" class="image-border" style="width: 200px" [alt]="attachment.filename || ''" />
              }
              @if (workspaceService.audioFiles[attachment.extension]) {
                <audio id="audio_player" controls="true">
                  <source [src]="attachment.url" type="audio/wav" />
                </audio>
              }
              @if (!workspaceService.audioFiles[attachment.extension] && !workspaceService.imageFiles[attachment.extension]) {
                <span class="gothic t-18">
                  <fa-icon icon="file" class="m-r-10" aria-hidden="true"></fa-icon>
                  <span>{{ attachment.filename }}</span>
                </span>
              }
              <button type="button" class="remove fa-button" [attr.aria-label]="('general.remove' | translate) + ' ' + (attachment.filename || '')" (click)="removeAttachment(attachment)">
                <fa-icon icon="times-circle" class="c-red" aria-hidden="true"></fa-icon>
              </button>
            </div>
          }
        </div>
      }
      @if (betaStringId && isBetaManager && createPostActive) {
        <div class="t-12">
          <div class="m-b-10">
            <button class="small-btn" (click)="populateBetaPostTemplate()">Inject Issue Template</button>
          </div>
          <div>
            <label>
              <input type="checkbox" [(ngModel)]="pinToBetaProgram" />
              &nbsp; Pin to top of Beta Program
            </label>
          </div>
          @if (pinToBetaProgram) {
            <div class="m-l-20">
              <label>
                <input type="checkbox" [(ngModel)]="allowCommentsOnPin" />
                &nbsp; Allow comments on this pinned post. Pinned posts are read-only unless you check this.
              </label>
            </div>
          }
          <div>
            <label>
              <input type="checkbox" [(ngModel)]="sendBetaProgramAnnouncementEmail" />
              &nbsp; Send an email with the contents of this post to every participant of {{ betaStringId }} program.
            </label>
          </div>
        </div>
      }
      <!--
        One toolbar: what to add (files, videos, articles), then how it goes out (signature,
        who can see it, scheduling), then Post. The visibility checkboxes that used to stack
        beside it are one menu now, named for its current setting.
      -->
      <div class="create-post-toolbar">
        <div class="create-post-toolbar__tools">
          @if (!uploadingFile) {
            <button type="button" class="create-post-tool" (click)="attachFile()" [ngbTooltip]="'File'">
              <fa-icon icon="paperclip" aria-hidden="true"></fa-icon>
              <span class="sr-only">File</span>
            </button>
            <button type="button" class="create-post-tool" (click)="showAttach('videos')" [ngbTooltip]="'Video'">
              <fa-icon icon="video" aria-hidden="true"></fa-icon>
              <span class="sr-only">Video</span>
            </button>
            <button type="button" class="create-post-tool" (click)="showAttach('articles')" [ngbTooltip]="'Article'">
              <fa-icon icon="newspaper" aria-hidden="true"></fa-icon>
              <span class="sr-only">Article</span>
            </button>
          }
          @if (!betaStringId) {
            <span class="create-post-toolbar__divider" aria-hidden="true"></span>
            @if (signature) {
              <button type="button" class="create-post-tool create-post-tool--labeled" [class.is-on]="signatureEnabled" [attr.aria-pressed]="signatureEnabled" (click)="toggleSignature()">
                <fa-icon icon="pencil-alt" aria-hidden="true"></fa-icon>
                @if (signatureEnabled) {
                  Signature on
                } @else {
                  Signature off
                }
              </button>
            } @else {
              <button type="button" class="create-post-tool create-post-tool--labeled" (click)="openSignatureSettings()">
                <fa-icon icon="pencil-alt" aria-hidden="true"></fa-icon>
                Add a signature
              </button>
            }
            @if (userService.user?.is_sphere_admin || userService.isEmployee) {
              <div ngbDropdown class="d-inline-block" placement="top-start">
                <button type="button" class="create-post-tool create-post-tool--labeled" ngbDropdownToggle aria-haspopup="true">
                  <fa-icon icon="users" aria-hidden="true"></fa-icon>
                  {{ audienceLabel }}
                </button>
                <div ngbDropdownMenu class="create-post-audience" role="group" aria-labelledby="create-post-audience-title">
                  <div class="create-post-audience__title" id="create-post-audience-title">Who can see this post</div>
                  @if (userService.user?.is_sphere_admin) {
                    <label class="create-post-audience__option">
                      <input type="checkbox" [(ngModel)]="sphereOnlyPost" (ngModelChange)="syncAudience()" />
                      Only in {{ env.studioOnePlusBrandName }}
                    </label>
                  }
                  @if (userService.isEmployee) {
                    <label class="create-post-audience__option">
                      <input type="checkbox" [(ngModel)]="employeeOnlyPost" (ngModelChange)="syncAudience()" />
                      Only employees
                    </label>
                  }
                </div>
              </div>
            }
            <!-- DENG-1294: moderators send a post straight to the community's Featured rail. -->
            @if (canFeature) {
              <button type="button" class="create-post-tool create-post-tool--labeled" [class.is-on]="featurePost" [attr.aria-pressed]="featurePost" (click)="toggleFeaturePost()">
                <!-- The label says which way it is, as the signature's does; a check when it's on. -->
                <fa-icon [icon]="featurePost ? 'check' : 'thumbtack'" aria-hidden="true"></fa-icon>
                @if (featurePost) {
                  Pinned to featured rail
                } @else {
                  Pin to featured rail
                }
              </button>
            }
          }
          @if (features.schedulePostsForBeta && betaStringId && !scheduledPost) {
            <button type="button" class="create-post-tool create-post-tool--labeled" (click)="onScheduleChange($event)">
              <fa-icon icon="clock" aria-hidden="true"></fa-icon>
              Schedule
            </button>
          }
          @if (scheduledPost) {
            <span class="create-post-tool create-post-tool--labeled is-on">
              <fa-icon icon="clock" aria-hidden="true"></fa-icon>
              {{ scheduledPostDate | date: "MMM Do" }} at {{ scheduledPostDate | date: "h:mma" }}
              <button type="button" class="fa-button m-l-5" (click)="onScheduleChange($event)">
                <fa-icon icon="times" aria-hidden="true"></fa-icon>
                <span class="sr-only">Remove schedule</span>
              </button>
            </span>
          }
        </div>
        <button type="button" class="send-btn no-margin" (click)="sendPost()" [class.disabled]="!postContent" [attr.aria-disabled]="!postContent">{{ "general.send" | translate }}</button>
      </div>
    }
  }
  <!-- The picked community won't take this member's posts: they can still pick another. -->
  @if (!betaStringId && communityChoices && communityChoices.length > 1 && !awaitingCommunity && !postGate.ok && user) {
    <ng-container *ngTemplateOutlet="communityChoice"></ng-container>
  }
  @if (!betaStringId && postGate.reason === "profile_not_public") {
    <div class="warning text-center">
      <p>{{ "com.profile_needs_public" | translate }}</p>
      <a class="small-btn blue" role="link" tabindex="0" (click)="makePublic()" (keydown.enter)="makePublic()">{{ "com.make_public" | translate }}</a>
    </div>
  }
  @if (!betaStringId && postGate.reason === "needs_fsp_plus") {
    <div class="warning text-center">
      <fui-heading [text]="'sphere.welcome.welcome' | translate" textAlign="center" fontSize="f6" level="2"></fui-heading>
      <p>
        {{ "com.free_browse" | translate: { appName: "My Fender", subName: env.studioOnePlusBrandName } }}
      </p>
      <ul class="list-inline">
        <li class="list-inline-item">
          <button class="btn btn-primary" type="button" (click)="upgrade()">{{ "onboarding.join_now" | translate }}</button>
        </li>
        <li class="list-inline-item">
          <button class="btn btn-secondary" type="button" (click)="watchVideo()">{{ "general.learn_more" | translate }}</button>
        </li>
      </ul>
    </div>
  }
  @if (!betaStringId && postGate.reason === "needs_fender_play") {
    <div class="warning text-center">
      <fui-heading text="Fender Play required" textAlign="center" fontSize="f6" level="2"></fui-heading>
      <p>Subscribe to Fender Play to post in this community.</p>
      <ul class="list-inline">
        <li class="list-inline-item">
          <a class="btn btn-primary" [href]="env.fenderPlayUrl" target="_blank" rel="noopener">Subscribe to Fender Play</a>
        </li>
      </ul>
    </div>
  }
  @if (!betaStringId && postGate.reason === "needs_product_ownership") {
    <div class="warning text-center">
      <fui-heading text="Product ownership required" textAlign="center" fontSize="f6" level="2"></fui-heading>
      <p>Register one of the following products with your account to post in this community.</p>
      @if (requiredProductsForDisplay?.length) {
        <div class="d-flex flex-wrap justify-content-center m-t-15" style="gap: 10px">
          @for (product of requiredProductsForDisplay; track product.type + ":" + product.value) {
            <div class="secondary-content-bg p-x-10 p-y-10 border-radius20 d-flex align-items-center" style="min-width: 180px; max-width: 260px">
              @if (product.image) {
                <img [src]="product.image" [alt]="product.label || product.value" style="width: 48px; height: 48px; object-fit: cover; border-radius: 6px; margin-right: 10px" />
              }
              <span class="t-14 brandfont-black">{{ product.label || product.value }}</span>
            </div>
          }
        </div>
      }
      <ul class="list-inline m-t-20">
        <li class="list-inline-item">
          <button class="btn btn-primary" type="button" (click)="registerProduct()">Register a product</button>
        </li>
        <li class="list-inline-item">
          <button class="btn btn-secondary" type="button" (click)="shopNow()">Shop now</button>
        </li>
      </ul>
    </div>
  }
  @if (!betaStringId && postGate.reason === "needs_group_membership") {
    <div class="warning text-center">
      <fui-heading text="Group membership required" textAlign="center" fontSize="f6" level="2"></fui-heading>
      <p>Only members of specific groups can post in this community. Contact a community admin if you believe you should have access.</p>
    </div>
  }
  @if (postGate.reason === "signed_out" && !betaStringId) {
    <div class="warning text-center">
      <p>Sign in or create an account to post and reply.</p>
      <button class="btn btn-primary" type="button" (click)="promptSignIn()">Sign in or create account</button>
    </div>
  }
</div>

<!--
  DISABLED 2026-09-15 — see `sendPost()`. Nothing opens this template any more; it is kept
  rather than deleted so re-enabling is a one-line change in the component. If it comes back,
  it should trigger only for private/NDA programs, not every enrollment.
-->
<ng-template #confirmPublicPostModal let-modal>
  <div class="modal-header">
    <h4 class="modal-title" id="hardware-history-details">Please confirm</h4>
    <button ngbAutofocus class="close" aria-label="Close" (click)="modal.dismiss('Cross click')">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
  <div class="modal-body">
    You are currently attempting to post in the public Community. This means that your post will be visible to all users. You are enrolled in the following Beta Programs, so please confirm that you
    would like to post publicly.

    @for (beta of betaService.enrolledBetaFeatures; track beta) {
      <div class="m-y-20">
        <strong>{{ beta.title }}</strong>
        <a class="btn btn-primary" [routerLink]="['/beta/' + beta.string_id + '/channels/all_posts']" (click)="moveToBeta(); modal.dismiss('Cross click')">Post in {{ beta.title }} instead.</a>
      </div>
    }

    <hr />
    <br />
    <button class="btn btn-primary" (click)="modal.dismiss('Cross click'); sendPost(false, true)">Post in Public Community</button>
  </div>
</ng-template>

<!-- Where the post goes, when the composer isn't tied to one community (`communityChoices`). -->
<ng-template #communityChoice>
  <div class="create-post-community">
    <label class="create-post-community__label" for="create-post-community">{{ "com.post_in" | translate }}</label>
    <select
      id="create-post-community"
      class="form-control"
      [class.is-unset]="awaitingCommunity"
      [attr.aria-invalid]="fieldError === 'community' ? 'true' : null"
      [attr.aria-describedby]="communityDescribedBy"
      [ngModel]="communityStringId"
      (ngModelChange)="chooseCommunity($event)"
    >
      <option value="" disabled>{{ "com.choose_community" | translate }}</option>
      @for (choice of communityChoices; track choice.stringId) {
        <option [value]="choice.stringId">{{ choice.title }}</option>
      }
    </select>
  </div>
</ng-template>
